<script lang="ts">
  import { onMount, onDestroy } from 'svelte';
  import { navigate } from '@shared/router/navigation';
  import { sessionStore } from '@features/auth/session/model/store.svelte';
  import { formCrmEnabled } from '@shared/config/features';
  import { canViewMembers, canManageTenant, canViewSiteMembers } from '@shared/types/session';
  import type { SessionUser } from '@shared/types/session';

  type Role = SessionUser['role'];
  type ActiveKey =
    | 'dashboard'
    | 'firms'
    | 'lawyers'
    | 'site-members'
    | 'forms'
    | 'form-submissions'
    | 'master-products'
    | 'third-party-solutions'
    | 'master-problems'
    | 'admin-organizations'
    | 'members'
    | null;

  interface Props {
    routeName: string;
  }

  const { routeName }: Props = $props();

  const storageKey = 'lpcrm.sidebar.collapsed';

  let collapsed = $state(false);

  type NavSection = 'main' | 'admin';
  type NavItem = {
    key: NonNullable<ActiveKey>;
    href: string;
    label: string;
    section: NavSection;
    /** ロールに対する可視判定。backend の権限が真実で、これは導線整理用の UI ミラー。 */
    visible: (role: Role) => boolean;
  };

  // 業務メニューは全ロール可視 (編集可否は各ページの UI ミラーが担当)。
  const always = (): boolean => true;

  const items: NavItem[] = [
    { key: 'firms', href: '/firms', label: '法律事務所', section: 'main', visible: always },
    { key: 'lawyers', href: '/lawyers', label: '弁護士', section: 'main', visible: always },
    // 会員 = 公開サイトのマイページ登録者。readonly は backend が 403 で弾くので導線も出さない。
    {
      key: 'site-members',
      href: '/site-members',
      label: '会員',
      section: 'main',
      visible: canViewSiteMembers,
    },
    { key: 'forms', href: '/forms', label: 'フォーム管理', section: 'main', visible: always },
    {
      key: 'form-submissions',
      href: '/form-submissions',
      label: '受信トレイ',
      section: 'main',
      visible: always,
    },
    { key: 'master-products', href: '/products', label: '自社ソリューション', section: 'main', visible: always },
    {
      key: 'third-party-solutions',
      href: '/third-party-solutions',
      label: '外部ソリューション',
      section: 'main',
      visible: always,
    },
    { key: 'master-problems', href: '/master/problems', label: '課題', section: 'main', visible: always },
    // 管理メニューはロールで出し分け。メンバー = super_admin / org_admin、組織 = super_admin のみ。
    { key: 'members', href: '/members', label: 'メンバー', section: 'admin', visible: canViewMembers },
    {
      key: 'admin-organizations',
      href: '/admin/organizations',
      label: '組織',
      section: 'admin',
      visible: canManageTenant,
    },
  ];

  type NavGroup = { label: string; keys: NonNullable<ActiveKey>[] };

  // 業務メニュー (main) のグルーピング。管理メニューはアバターのポップオーバーに集約する。
  const groupDefs: NavGroup[] = [
    { label: '顧客DB', keys: ['firms', 'lawyers'] },
    // 会員は CRM の確定情報ではなく公開サイト側の登録者なので、顧客DB に混ぜず独立させる。
    { label: '公開サイト', keys: ['site-members'] },
    ...(formCrmEnabled
      ? [{ label: 'マーケティング', keys: ['forms', 'form-submissions'] satisfies NonNullable<ActiveKey>[] }]
      : []),
    { label: 'マスタ', keys: ['master-products', 'third-party-solutions', 'master-problems'] },
  ];

  /// ロールが未解決 (セッション読み込み中 / 失敗) のときは最小権限として扱い、権限に依存する
  /// 項目を出さない (fail closed)。全ロール可視の項目は `always` なのでそのまま残る。
  function isVisible(item: NavItem, role: Role | null): boolean {
    return item.visible(role ?? 'readonly');
  }

  // 業務メニューもロールで出し分ける (会員一覧は readonly に権限が無い)。項目が 1 つも
  // 残らないグループは見出しごと出さない。
  const groups = $derived.by<{ label: string; items: NavItem[] }[]>(() => {
    const role: Role | null = sessionStore.user?.role ?? null;
    return groupDefs
      .map((group) => ({
        label: group.label,
        items: group.keys
          .map((key) => items.find((item) => item.key === key)!)
          .filter((item) => isVisible(item, role)),
      }))
      .filter((group) => group.items.length > 0);
  });

  // 管理メニューはロールで出し分け (空ならポップオーバーのセクションごと非表示)。
  const adminItems = $derived.by(() => {
    const role: Role | null = sessionStore.user?.role ?? null;
    if (!role) return [];
    return items.filter((item) => item.section === 'admin' && item.visible(role));
  });

  const active = $derived<ActiveKey>(routeToActiveKey(routeName));

  // key → インライン SVG path 群 (LogoutButton と同じ line アイコン作法: viewBox 0 0 24 24 / stroke)。
  const iconPaths: Record<NonNullable<ActiveKey>, string[]> = {
    dashboard: ['M3 13h8V3H3zM13 21h8V3h-8zM3 21h8v-6H3z'],
    firms: ['M3 21h18', 'M5 21V7l7-4 7 4v14', 'M9 9h.01M9 13h.01M9 17h.01M15 9h.01M15 13h.01M15 17h.01'],
    lawyers: ['M12 12a4 4 0 100-8 4 4 0 000 8z', 'M4 21v-1a6 6 0 016-6h4a6 6 0 016 6v1'],
    // 会員証を思わせるカード + 人型。社内メンバー (2 人組アイコン) と取り違えないようにする。
    'site-members': [
      'M3 5h18v14H3z',
      'M9 11a2 2 0 100-4 2 2 0 000 4z',
      'M6 16c0-1.7 1.3-3 3-3s3 1.3 3 3',
      'M14 9h4M14 13h4',
    ],
    forms: ['M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z', 'M14 2v6h6', 'M8 13h8M8 17h5'],
    'form-submissions': ['M22 12h-6l-2 3h-4l-2-3H2', 'M5.45 5.11L2 12v6a2 2 0 002 2h16a2 2 0 002-2v-6l-3.45-6.89A2 2 0 0016.76 4H7.24a2 2 0 00-1.79 1.11z'],
    'master-products': ['M21 16V8l-9-5-9 5v8l9 5 9-5z', 'M3.3 7L12 12l8.7-5M12 22V12'],
    'third-party-solutions': ['M12 2l9 5-9 5-9-5 9-5z', 'M3 12l9 5 9-5', 'M3 17l9 5 9-5'],
    'master-problems': ['M9 11l3 3L22 4', 'M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11'],
    members: [
      'M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2',
      'M9 11a4 4 0 100-8 4 4 0 000 8z',
      'M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75',
    ],
    'admin-organizations': ['M3 3h7v7H3zM14 3h7v7h-7zM14 14h7v7h-7zM3 14h7v7H3z'],
  };

  function routeToActiveKey(name: string): ActiveKey {
    switch (name) {
      case 'firms-list':
      case 'firm-detail':
        return 'firms';
      case 'lawyers-list':
      case 'lawyer-detail':
        return 'lawyers';
      case 'site-members-list':
        return 'site-members';
      case 'forms-list':
      case 'form-detail':
        return 'forms';
      case 'form-submissions-list':
      case 'form-submission-detail':
        return 'form-submissions';
      case 'products-list':
      case 'product-detail':
        return 'master-products';
      case 'third-party-solutions-list':
      case 'third-party-solution-detail':
        return 'third-party-solutions';
      case 'problems-list':
      case 'problem-detail':
        return 'master-problems';
      case 'admin-organizations':
      case 'admin-organization-detail':
        return 'admin-organizations';
      case 'members':
        return 'members';
      case 'dashboard':
        return 'dashboard';
      default:
        return null;
    }
  }

  function roleLabel(role: Role | null): string {
    if (role === 'super_admin') return 'スーパー管理者';
    if (role === 'org_admin') return '組織管理者';
    if (role === 'member') return 'メンバー';
    if (role === 'readonly') return '閲覧のみ';
    return '不明';
  }

  // --- ユーザーポップオーバー (アバター + 管理メニュー + ログアウト) ---
  let menuOpen = $state(false);
  let menuEl: HTMLDivElement | null = $state(null);

  const avatarInitial = $derived(sessionStore.user?.name?.trim().charAt(0) || '?');

  function toggleMenu(): void {
    menuOpen = !menuOpen;
  }

  function closeMenu(): void {
    menuOpen = false;
  }

  let loggingOut = $state(false);

  async function handleLogout(): Promise<void> {
    if (loggingOut) return;
    loggingOut = true;
    try {
      await sessionStore.logout();
      closeMenu();
    } catch (error) {
      console.error('ログアウトエラー:', error);
    } finally {
      loggingOut = false;
    }
  }

  // 外側クリックで閉じる (FilterChip.svelte パターン)。
  function handleMenuDocClick(ev: MouseEvent): void {
    if (!menuOpen) return;
    if (menuEl && !menuEl.contains(ev.target as Node)) closeMenu();
  }

  // Esc で閉じる (Modal.svelte パターン)。
  function handleMenuKey(ev: KeyboardEvent): void {
    if (ev.key === 'Escape') closeMenu();
  }

  onMount(() => {
    collapsed = window.localStorage.getItem(storageKey) === 'true';
    document.addEventListener('click', handleMenuDocClick);
  });

  onDestroy(() => {
    document.removeEventListener('click', handleMenuDocClick);
  });

  function toggleSidebar(): void {
    collapsed = !collapsed;
    window.localStorage.setItem(storageKey, String(collapsed));
  }

  function handleClick(ev: MouseEvent, href: string): void {
    if (ev.metaKey || ev.ctrlKey || ev.shiftKey || ev.altKey || ev.button !== 0) return;
    ev.preventDefault();
    navigate(href);
  }

  function handleMenuItemClick(ev: MouseEvent, href: string): void {
    if (ev.metaKey || ev.ctrlKey || ev.shiftKey || ev.altKey || ev.button !== 0) return;
    ev.preventDefault();
    navigate(href);
    closeMenu();
  }
</script>

<svelte:window onkeydown={handleMenuKey} />

<aside class="sidebar" class:collapsed data-testid="sidebar" aria-label="メインナビゲーション">
  <div class="sidebar-header">
    <a class="brand" href="/firms" title="LP CRM" aria-label="LP CRM" onclick={(ev) => handleClick(ev, '/firms')}>
      <span class="brand-full">LP CRM</span>
      <span class="brand-compact" aria-hidden="true">LP</span>
    </a>
    <button
      class="sidebar-toggle"
      type="button"
      aria-label={collapsed ? 'サイドバーを開く' : 'サイドバーを閉じる'}
      aria-expanded={!collapsed}
      title={collapsed ? 'サイドバーを開く' : 'サイドバーを閉じる'}
      onclick={toggleSidebar}
    >
      <span aria-hidden="true">{collapsed ? '›' : '‹'}</span>
    </button>
  </div>
  <nav>
    {#each groups as group (group.label)}
      <div class="nav-group">
        <div class="nav-group-label">{group.label}</div>
        <div class="nav-group-items">
          {#each group.items as item (item.key)}
            <a
              href={item.href}
              class:active={active === item.key}
              title={item.label}
              onclick={(ev) => handleClick(ev, item.href)}
            >
              <svg
                class="nav-icon"
                aria-hidden="true"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                {#each iconPaths[item.key] as d (d)}
                  <path {d} />
                {/each}
              </svg>
              <span class="nav-label">{item.label}</span>
            </a>
          {/each}
        </div>
      </div>
    {/each}
  </nav>

  <div class="user-menu" bind:this={menuEl}>
    {#if menuOpen && sessionStore.user}
      <div class="popover" role="menu" aria-label="アカウントメニュー">
        <div class="popover-header">
          <div class="pname">{sessionStore.user.name}</div>
          <div class="pemail">{sessionStore.user.email}</div>
          <div class="prole">{roleLabel(sessionStore.user.role)}</div>
        </div>

        {#if adminItems.length > 0}
          <div class="popover-section">
            <div class="section-label">管理</div>
            {#each adminItems as item (item.key)}
              <a
                href={item.href}
                class="menu-item"
                class:active={active === item.key}
                role="menuitem"
                onclick={(ev) => handleMenuItemClick(ev, item.href)}
              >
                <svg
                  class="nav-icon"
                  aria-hidden="true"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  {#each iconPaths[item.key] as d (d)}
                    <path {d} />
                  {/each}
                </svg>
                <span>{item.label}</span>
              </a>
            {/each}
          </div>
        {/if}

        <div class="popover-footer">
          <button
            type="button"
            class="menu-item"
            onclick={handleLogout}
            disabled={loggingOut}
          >
            <svg
              class="nav-icon"
              aria-hidden="true"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
            </svg>
            <span>{loggingOut ? 'ログアウト中…' : 'ログアウト'}</span>
          </button>
        </div>
      </div>
    {/if}

    {#if sessionStore.loading}
      <div class="trigger-status">セッション読み込み中…</div>
    {:else if sessionStore.error}
      <div class="trigger-status error-text">{sessionStore.error}</div>
    {:else if sessionStore.user}
      <button
        type="button"
        class="trigger"
        class:open={menuOpen}
        aria-haspopup="menu"
        aria-expanded={menuOpen}
        title={sessionStore.user.name}
        onclick={toggleMenu}
      >
        <span class="avatar" aria-hidden="true">{avatarInitial}</span>
        <span class="trigger-name">{sessionStore.user.name}</span>
        <span class="chevron" aria-hidden="true">⌄</span>
      </button>
    {:else}
      <div class="trigger-status">(未ログイン)</div>
    {/if}
  </div>
</aside>

<style>
  .sidebar {
    position: sticky;
    top: 0;
    /* grid セル (.app-shell) の stretch で main の高さまで伸びるのを防ぎ、
       ビューポート高さに固定する。これで最下部のアバターが常に画面内に残る。 */
    align-self: start;
    height: 100vh;
    width: 220px;
    display: flex;
    flex-direction: column;
    transition:
      width 160ms ease,
      padding 160ms ease;
  }

  .sidebar.collapsed {
    width: 56px;
    padding-left: 8px;
    padding-right: 8px;
  }

  .sidebar-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    margin-bottom: 16px;
  }

  .brand {
    color: #fff;
    font-size: 16px;
    font-weight: 700;
    line-height: 1.2;
    white-space: nowrap;
  }

  .brand:hover {
    text-decoration: none;
  }

  .brand-compact {
    display: none;
  }

  nav {
    display: flex;
    flex: 1;
    flex-direction: column;
    /* 業務メニューが多いときは nav 内だけスクロール。min-height:0 は
       flex 子要素が overflow できるようにするため (既定の auto だと縮まない)。 */
    min-height: 0;
    overflow-y: auto;
  }

  nav a {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  .nav-icon {
    flex: 0 0 auto;
    width: 18px;
    height: 18px;
  }

  .sidebar-toggle {
    display: inline-grid;
    place-items: center;
    flex: 0 0 auto;
    width: 28px;
    height: 28px;
    border: 1px solid #334155;
    border-radius: 6px;
    background: #1f2937;
    color: #e5e7eb;
    font-size: 20px;
    line-height: 1;
  }

  .sidebar-toggle:hover {
    background: #273449;
  }

  .error-text {
    color: #fca5a5;
  }

  .sidebar.collapsed .sidebar-header {
    justify-content: center;
  }

  .sidebar.collapsed .brand-full,
  .sidebar.collapsed .nav-label {
    display: none;
  }

  .sidebar.collapsed .brand-compact {
    display: inline;
  }

  .sidebar.collapsed .brand {
    position: absolute;
    width: 1px;
    height: 1px;
    overflow: hidden;
    clip: rect(0 0 0 0);
    white-space: nowrap;
  }

  .sidebar.collapsed nav {
    align-items: center;
  }

  .nav-group {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .nav-group + .nav-group {
    margin-top: 14px;
    padding-top: 14px;
    border-top: 1px solid rgba(255, 255, 255, 0.1);
  }

  .nav-group-label {
    padding: 0 8px;
    color: #94a3b8;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0;
  }

  .nav-group-items {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .sidebar.collapsed nav a {
    display: grid;
    place-items: center;
    width: 40px;
    height: 40px;
    padding: 0;
    font-weight: 700;
  }

  .sidebar.collapsed .nav-group-label {
    display: none;
  }

  .sidebar.collapsed .nav-group + .nav-group {
    margin-top: 8px;
  }

  .sidebar.collapsed .nav-icon {
    width: 20px;
    height: 20px;
  }

  /* --- ユーザーポップオーバー --- */
  .user-menu {
    position: relative;
    margin-top: 24px;
  }

  .trigger {
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
    padding: 6px 8px;
    border: 1px solid transparent;
    border-radius: 8px;
    background: transparent;
    color: #e5e7eb;
    font-size: 13px;
    text-align: left;
    cursor: pointer;
  }

  .trigger:hover,
  .trigger.open {
    background: #1f2937;
    border-color: #334155;
  }

  .avatar {
    display: inline-grid;
    place-items: center;
    flex: 0 0 auto;
    width: 28px;
    height: 28px;
    border-radius: 50%;
    background: #2563eb;
    color: #fff;
    font-size: 13px;
    font-weight: 700;
    text-transform: uppercase;
  }

  .trigger-name {
    flex: 1 1 auto;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }

  .chevron {
    flex: 0 0 auto;
    font-size: 14px;
    color: #94a3b8;
    transition: transform 160ms ease;
  }

  .trigger.open .chevron {
    transform: rotate(180deg);
  }

  .trigger-status {
    padding: 6px 8px;
    font-size: 11px;
    color: #94a3b8;
  }

  /* ポップオーバーはトリガー直上に開く。 */
  .popover {
    position: absolute;
    bottom: calc(100% + 8px);
    left: 0;
    z-index: 80;
    width: max-content;
    min-width: 220px;
    max-width: 280px;
    padding: 8px;
    border: 1px solid #334155;
    border-radius: 10px;
    background: #111827;
    box-shadow: 0 12px 32px rgba(0, 0, 0, 0.45);
  }

  .popover-header {
    padding: 8px 10px;
  }

  .pname {
    color: #f8fafc;
    font-size: 14px;
    font-weight: 700;
  }

  .pemail {
    margin-top: 2px;
    color: #94a3b8;
    font-size: 11px;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }

  .prole {
    margin-top: 4px;
    color: #60a5fa;
    font-size: 11px;
    font-weight: 700;
  }

  .popover-section {
    margin-top: 4px;
    padding-top: 8px;
    border-top: 1px solid rgba(255, 255, 255, 0.1);
  }

  .section-label {
    padding: 0 10px 4px;
    color: #94a3b8;
    font-size: 11px;
    font-weight: 700;
  }

  .menu-item {
    display: flex;
    align-items: center;
    gap: 10px;
    width: 100%;
    padding: 8px 10px;
    border: none;
    border-radius: 6px;
    background: transparent;
    color: #e5e7eb;
    font-family: inherit;
    font-size: 13px;
    text-align: left;
    cursor: pointer;
  }

  .menu-item:hover {
    background: #1f2937;
    text-decoration: none;
  }

  .menu-item.active {
    background: #1d4ed8;
    color: #fff;
  }

  .menu-item:disabled {
    opacity: 0.6;
    cursor: default;
  }

  .popover-footer {
    margin-top: 4px;
    padding-top: 8px;
    border-top: 1px solid rgba(255, 255, 255, 0.1);
  }

  /* collapsed (56px) 時はアバターのみ。名前/シェブロンを隠し、ポップオーバーは右へ逃がす。 */
  .sidebar.collapsed .trigger {
    justify-content: center;
    padding: 6px 0;
  }

  .sidebar.collapsed .trigger-name,
  .sidebar.collapsed .chevron {
    display: none;
  }

  .sidebar.collapsed .popover {
    left: calc(100% + 8px);
    bottom: 0;
  }

  @media (max-width: 768px) {
    .sidebar {
      position: sticky;
      width: 100%;
      /* トップバー化するため viewport 固定を解除 (デスクトップの height:100vh を打ち消す)。 */
      height: auto;
      min-height: auto;
      overflow: visible;
    }

    /* 旧 .meta と同様、モバイルの横並びナビではユーザーメニューを出さない。 */
    .user-menu {
      display: none;
    }

    .sidebar.collapsed {
      width: 100%;
      padding: 8px 12px;
    }

    .sidebar.collapsed .sidebar-header {
      justify-content: space-between;
      margin-bottom: 0;
    }

    .sidebar.collapsed .brand {
      position: static;
      width: auto;
      height: auto;
      overflow: visible;
      clip: auto;
    }

    .sidebar.collapsed .brand-full {
      display: inline;
    }

    .sidebar.collapsed .brand-compact {
      display: none;
    }

    .sidebar.collapsed nav {
      display: none;
    }

    .nav-group {
      flex: 0 0 auto;
      min-width: max-content;
    }

    .nav-group + .nav-group {
      margin-top: 0;
    }

    .nav-group-label {
      padding: 0 8px 2px;
      font-size: 10px;
    }

    .nav-group-items {
      flex-direction: row;
    }
  }
</style>
